import dayjs from "dayjs";

/**
 * Async delay utility
 * @param ms - Milliseconds to delay
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Formats a date string or Date object as DD/MM/YYYY (UTC)
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string in DD/MM/YYYY format
 */
export const formatDateAsLocale = (dateString: string | Date): string => {
  const formatted = dayjs.utc(dateString);
  if (!formatted.isValid()) {
    return String(dateString);
  }
  return formatted.format("DD/MM/YYYY");
};

/**
 * Formats a date string or Date object as DD/MM/YYYY HH:mm (UTC, no seconds)
 * @param dateString - The date string or Date object to format
 * @returns Formatted date-time string in DD/MM/YYYY HH:mm format
 */
export const formatDateTimeShort = (dateString: string | Date): string => {
  const formatted = dayjs.utc(dateString);
  if (!formatted.isValid()) {
    return String(dateString);
  }
  return formatted.format("DD/MM/YYYY HH:mm");
};

/**
 * Formats a date string or Date object as DD/MM/YYYY, HH:mm:ss (UTC)
 * @param dateString - The date string or Date object to format
 * @returns Formatted date-time string in DD/MM/YYYY, HH:mm:ss format (UTC)
 */
export const formatDateTimeUTC = (dateString: string | Date): string => {
  const formatted = dayjs.utc(dateString);
  if (!formatted.isValid()) {
    return String(dateString);
  }
  return formatted.format("DD/MM/YYYY, HH:mm:ss");
};

/**
 * Converts a local datetime string to ISO 8601 format with timezone
 * @param dateTime - Local datetime string in format "2024-01-01T00:00"
 * @returns ISO 8601 datetime string with timezone: "2024-01-01T00:00:00+00:00"
 */
export const formatToISO8601WithTimezone = (dateTime: string): string => {
  return `${dateTime}:00+00:00`;
};

/**
 * Normalizes a datetime string to datetime-local format (YYYY-MM-DDTHH:mm) in UTC
 * Handles various input formats including full ISO 8601 with timezone
 * @param dateTime - Any datetime string (e.g., "2024-01-01T00:00:00Z", "2024-01-01T00:00")
 * @returns Datetime string in format "YYYY-MM-DDTHH:mm" for form inputs, or empty string if invalid
 */
export const normalizeDateTimeForForm = (dateTime: string): string => {
  if (!dateTime) return "";

  const parsed = dayjs.utc(dateTime);
  if (!parsed.isValid()) {
    return "";
  }

  return parsed.format("YYYY-MM-DDTHH:mm");
};
